'use client';

import { type ReactNode, useState } from 'react';
import { useRootClose } from 'react-overlays';
import { useMedia } from 'react-use';

import { useModalTransitions } from '@/hooks/useModalTransitions';
import { useUniqueId } from '@/hooks/useUniqueId';

import { DropdownContextProvider } from './context';

export interface Props {
    autoClose?: boolean;
    children: ReactNode;
    onToggle?: (isOpen: boolean) => void;
}

export function Dropdown({ autoClose = true, children, onToggle }: Props) {
    const isReducedMotion = useMedia(`(prefers-reduced-motion: reduce)`, false);
    const dropdown = useModalTransitions({
        onClosed: onToggle ? () => onToggle(false) : undefined,
        onOpened: onToggle ? () => onToggle(true) : undefined,
        transitionDuration: isReducedMotion ? 0 : 200,
    });
    const triggerId = useUniqueId('dropdown-trigger-');
    const dropdownId = useUniqueId('dropdown-list-');
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

    function handleRootClose(event: Event) {
        if (!autoClose && popperElement && popperElement.contains(event.target as HTMLElement)) {
            return;
        }

        dropdown.close();
    }

    useRootClose(referenceElement, handleRootClose, {
        disabled: !dropdown.isMounted,
    });

    return (
        <DropdownContextProvider
            dropdownId={dropdownId}
            popperReference={popperElement}
            setPopperReference={setPopperElement}
            setTriggerReference={setReferenceElement}
            triggerId={triggerId}
            triggerReference={referenceElement}
            {...dropdown}
        >
            {children}
        </DropdownContextProvider>
    );
}
